/**
 * AGENT PAYOUTS (2026-11-01).
 *
 * This is the page that fixes "no way to register their account". Bank details are typed on Stripe, never
 * through this app: the button asks the API for a hosted onboarding URL and sends the browser there. On
 * return the status is re-read, and any commission that accrued before the account existed is released.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { getApiBaseUrl, getAuthHeaders } from "../../utils/apiHelpers";
import {
  AgentPageState,
  Badge,
  formatDate,
  money,
  payoutTone,
  styles,
  useAgentResource,
} from "./agentShared";

function Stat({ label, value, hint }) {
  return (
    <div style={styles.stat}>
      <span style={styles.statLabel}>{label}</span>
      <div style={styles.statValue}>{value}</div>
      {hint ? <span style={styles.muted}>{hint}</span> : null}
    </div>
  );
}

/** "every 24 hours" reads worse than "daily", and 336 hours is a fortnight. */
function frequencyLabel(hours) {
  const value = Number(hours);
  if (!Number.isFinite(value) || value <= 0) return undefined;
  if (value === 24) return "Paid daily";
  if (value === 168) return "Paid weekly";
  if (value === 336) return "Paid every 2 weeks";
  if (value === 720) return "Paid monthly";
  return `Paid every ${value} hours`;
}

export default function AgentPayouts() {
  const { loading, error, unavailable, data, reload } = useAgentResource(
    "/api/platform-plans/referral/payouts",
  );
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState("");
  const [actionError, setActionError] = useState("");
  const [notice, setNotice] = useState("");

  const returned = params.get("connect");

  // Coming back from Stripe: the status on the server has just changed, so re-read it rather than trusting
  // whatever this page loaded a moment ago.
  useEffect(() => {
    if (returned) reload();
  }, [returned, reload]);

  const connect = async (mode) => {
    setBusy(mode);
    setActionError("");
    setNotice("");
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.post(
        `${getApiBaseUrl()}/api/platform-plans/referral/connect/link`,
        { mode },
        { headers },
      );
      const url = res.data?.url;
      if (!url) {
        setActionError("Stripe did not return a link. Please try again.");
        return;
      }
      window.location.assign(url);
    } catch (err) {
      setActionError(
        err?.response?.data?.message ||
          "Unable to start payout setup right now. Please try again.",
      );
    } finally {
      setBusy("");
    }
  };

  const dismissNotice = () => {
    const next = new URLSearchParams(params);
    next.delete("connect");
    setParams(next, { replace: true });
    setNotice("");
  };

  const conn = data?.connect || {};
  const ready = Boolean(conn.payoutsEnabled);

  return (
    <AgentPageState
      loading={loading}
      error={error}
      unavailable={unavailable}
      reload={reload}
    >
      <div style={styles.page}>
        <h1 style={styles.h1}>Payouts</h1>
        <p style={{ ...styles.muted, marginTop: 0 }}>
          Commission is paid to your own bank account through Stripe.
        </p>

        {returned === "return" && !ready ? (
          <div style={styles.notice}>
            Stripe needs a little more information before payouts can start.{" "}
            <button
              type="button"
              style={{
                border: "none",
                background: "none",
                color: "#1d4ed8",
                fontWeight: 600,
                cursor: "pointer",
                padding: 0,
              }}
              onClick={dismissNotice}
            >
              Dismiss
            </button>
          </div>
        ) : null}
        {notice ? <div style={styles.notice}>{notice}</div> : null}
        {actionError ? <div style={styles.err}>{actionError}</div> : null}

        <div style={styles.card}>
          <h2 style={styles.h2}>Payout account</h2>

          {!conn.connected ? (
            <>
              <p style={{ marginTop: 0 }}>
                You haven’t added a bank account yet. Commission you have already
                earned is safe — it is recorded and will be released as soon as the
                account is set up.
              </p>
              <button
                type="button"
                style={styles.btn}
                onClick={() => connect("onboarding")}
                disabled={busy === "onboarding"}
              >
                {busy === "onboarding" ? "Opening Stripe…" : "Set up payouts"}
              </button>
            </>
          ) : (
            <>
              <div style={styles.statGrid}>
                <Stat
                  label="Status"
                  value={ready ? "Ready" : "Incomplete"}
                  hint={
                    ready
                      ? "Stripe can send you money"
                      : "Stripe needs a few details from you"
                  }
                />
                <Stat
                  label="Bank account"
                  value={conn.bank?.last4 ? `•••• ${conn.bank.last4}` : "—"}
                  hint={conn.bank?.bankName || "Add one on Stripe"}
                />
                <Stat
                  label="Minimum payout"
                  value={
                    Number.isFinite(data?.minimumPayoutCents)
                      ? money(data.minimumPayoutCents)
                      : "—"
                  }
                  hint={frequencyLabel(data?.payoutFrequencyHours)}
                />
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {!ready ? (
                  <button
                    type="button"
                    style={styles.btn}
                    onClick={() => connect("onboarding")}
                    disabled={busy === "onboarding"}
                  >
                    {busy === "onboarding" ? "Opening Stripe…" : "Finish setup"}
                  </button>
                ) : null}
                <button
                  type="button"
                  style={styles.btnGhost}
                  onClick={() => connect("dashboard")}
                  disabled={busy === "dashboard" || !conn.detailsSubmitted}
                >
                  {busy === "dashboard"
                    ? "Opening Stripe…"
                    : "Manage bank details on Stripe"}
                </button>
              </div>

              {Array.isArray(conn.requirementsDue) &&
              conn.requirementsDue.length > 0 ? (
                <p style={{ ...styles.muted, marginBottom: 0 }}>
                  Stripe still needs a few details — your bank account and identity
                  information. Select “Finish setup” to add them.
                </p>
              ) : null}
            </>
          )}
        </div>

        <div style={styles.statGrid}>
          <Stat
            label="Paid out"
            value={money(data?.totals?.paidOutCents)}
            hint="Already in your bank"
          />
          <Stat
            label="In flight"
            value={money(data?.totals?.inFlightCents)}
            hint="Approved for a payout"
          />
          <Stat
            label="On hold"
            value={money(data?.totals?.heldCents)}
            hint="Within the hold window"
          />
        </div>

        <div style={styles.card}>
          <h2 style={styles.h2}>Payout history</h2>
          {Array.isArray(data?.jobs) && data.jobs.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Scheduled</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Items</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Stripe transfer</th>
                </tr>
              </thead>
              <tbody>
                {data.jobs.map((job) => (
                  <tr key={job.id}>
                    <td style={styles.td}>
                      {formatDate(job.scheduledForAt || job.createdAt)}
                    </td>
                    <td style={styles.td}>
                      <strong>{money(job.amountCents)}</strong>
                    </td>
                    <td style={styles.td}>{job.entries}</td>
                    <td style={styles.td}>
                      <Badge tone={payoutTone(job.status)} />
                      {job.failureReason ? (
                        <div style={styles.muted}>{job.failureReason}</div>
                      ) : null}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.muted}>{job.stripeTransferId || "—"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={styles.empty}>
              No payouts yet. Your first payout is created automatically once you have
              earned at least the minimum and the hold period has passed.
            </div>
          )}
        </div>
      </div>
    </AgentPageState>
  );
}
